import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/shared/services/api.service';
import { SubscriptionAccessService } from 'src/app/shared/services/subscription-access.service';

@Component({
  selector: 'app-success-payment',
  templateUrl: './success-payment.component.html',
  styleUrls: ['./success-payment.component.css']
})
export class SuccessPaymentComponent implements OnInit {
  paymentDetails: any;
  paymentType: 'stripe' | 'razorpay' | null = null;
  loading = true;

  constructor(
    private paymentService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private toaster: ToastrService,
    private subscriptionAccessService: SubscriptionAccessService
  ) {}

  ngOnInit(): void {
    const sessionId = this.route.snapshot.queryParamMap.get('session_id');
    const paymentId = this.route.snapshot.queryParamMap.get('payment_id');
    const subscriptionId = this.route.snapshot.queryParamMap.get('subscription_id');

    if (sessionId) {
      // Handle Stripe payment
      this.paymentType = 'stripe';
      this.validateStripePayment(sessionId);
    } else if (paymentId) {
      // Handle Razorpay payment
      this.paymentType = 'razorpay';
      this.validateRazorpayPayment(paymentId, subscriptionId);
    } else {
      console.warn('No payment identifier found in URL.');
      this.toaster.warning('Invalid payment session.');
      this.loading = false;
    }
  }

  validateStripePayment(sessionId: string): void {
    this.paymentService.savePaymentDetails({ session_id: sessionId }).subscribe({
      next: (res: any) => {
        this.paymentDetails = {
          ...res.session,
          paymentType: 'stripe',
          amount: res.session.amount_total / 100, // Convert from paise to rupees
          currency: res.session.currency?.toUpperCase() || 'INR',
          email: res.session.customer_details?.email,
          paymentId: res.session.id
        };
        this.toaster.success('Payment successful!');
        this.loading = false;
        console.log('Stripe session:', this.paymentDetails);
      },
      error: (err: any) => {
        console.error('Error occurred while saving payment details:', err);
        this.toaster.error('Payment failed or session not found');
        this.loading = false;
      },
      complete: () => {
        console.log('Payment fetch completed.');
      }
    });
  }

  validateRazorpayPayment(paymentId: string, subscriptionId: string | null): void {
    // First try to get subscription by payment_id using the dedicated endpoint
    this.paymentService.getSubscriptionByPaymentId(paymentId).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          const subscription = res.data;
          
          this.paymentDetails = {
            paymentType: 'razorpay',
            paymentId: paymentId,
            amount: subscription.total_amount || 0,
            currency: 'INR',
            email: subscription.organization?.email || '',
            subscription: subscription,
            metadata: subscription.metadata || {},
            duration: subscription.duration,
            membershipEndDate: subscription.membership_end_date
          };
          this.toaster.success('Payment successful!');
          this.loading = false;
          // ACC-004, ACC-007: Refresh subscription access after payment
          this.subscriptionAccessService.refreshSubscriptionAccess();
        } else {
          // Fallback: try to get from subscription list
          this.fallbackRazorpayValidation(paymentId, subscriptionId);
        }
      },
      error: (err: any) => {
        console.error('Error fetching subscription by payment ID:', err);
        // Fallback: try to get from subscription list
        this.fallbackRazorpayValidation(paymentId, subscriptionId);
      }
    });
  }

  fallbackRazorpayValidation(paymentId: string, subscriptionId: string | null): void {
    // Fallback: fetch all subscriptions and find matching one
    this.paymentService.getSubscriptionDetails({}).subscribe({
      next: (res: any) => {
        // Find subscription with matching payment_id in metadata
        let subscription = null;
        
        // Handle both array and single object responses
        const subscriptions = Array.isArray(res) ? res : (res ? [res] : []);
        
        subscription = subscriptions.find((sub: any) => {
          const metadata = typeof sub.metadata === 'string' 
            ? JSON.parse(sub.metadata) 
            : (sub.metadata || {});
          return metadata.razorpay_payment_id === paymentId || 
                 (subscriptionId && sub.id === parseInt(subscriptionId));
        });

        if (subscription) {
          const metadata = typeof subscription.metadata === 'string' 
            ? JSON.parse(subscription.metadata) 
            : (subscription.metadata || {});
          
          this.paymentDetails = {
            paymentType: 'razorpay',
            paymentId: paymentId,
            amount: subscription.total_amount || 0,
            currency: 'INR',
            email: '',
            subscription: subscription,
            metadata: metadata,
            duration: subscription.duration,
            membershipEndDate: subscription.membership_end_date
          };
          this.toaster.success('Payment successful!');
        } else {
          // Final fallback: create basic payment details
          this.paymentDetails = {
            paymentType: 'razorpay',
            paymentId: paymentId,
            amount: 0,
            currency: 'INR',
            email: ''
          };
          this.toaster.success('Payment successful!');
          // ACC-004, ACC-007: Refresh subscription access after payment
          this.subscriptionAccessService.refreshSubscriptionAccess();
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error fetching subscription details:', err);
        // Final fallback: create basic payment details
        this.paymentDetails = {
          paymentType: 'razorpay',
          paymentId: paymentId,
          amount: 0,
          currency: 'INR',
          email: ''
        };
        this.toaster.success('Payment successful!');
        this.loading = false;
      }
    });
  }
}
